import { HiOutlineShoppingCart } from "react-icons/hi";
import "./CartComponentStyles.css"
import React, { useRef, useState } from "react";
import PaymentDetailsComponent from "./PaymentDetailsComponent";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from '@stripe/stripe-js';
import { useTheme } from "../../../hooks/ThemeContext";
import CartCards from "./CartCards";
import TimeService from "../../../service/TimeService";
import ToastService from "../../../service/ToastService";
import { toast } from "react-toastify";
import { Tooltip } from 'react-tooltip'
import 'react-tooltip/dist/react-tooltip.css'
import Spinner from "../../Spinner/Spinner";
import DeliveryAddress from "./DeliveryAddress";
import PostcodeService from "../../../service/PostcodeService";
import { PostcodeServiceFactory } from "../../../service/PostcodeServiceFactory";
import { FIREBASE_APP } from "../../../Firebase";
import { getRemoteEnv } from '../../../config/remoteEnv';import { httpsCallableWithAppId } from '../../../service/CallableWrapper';
import { useOrderMode, ORDER_MODES } from "../../../hooks/OrderModeContext";
import OrderModeSelect from "../../OrderMode/OrderModeSelect";
import StorePauseBanner from "../../StorePause/StorePauseBanner";
import { getFunctions } from 'firebase/functions';
import { taxFromCharge } from '../../../service/TaxMath';


type Props = {
    cart: any[],
    setCart: React.Dispatch<React.SetStateAction<any[]>>,
    productData: any[],
    menuSettings: any[],
    openingHours: any,
    additionalCharges: any[],
    testMode?: boolean,
    singleStoreConfig: any
}

type Address = {
    line1: string;
    line2: string;
    city: string;
    country: string;
    postcode: string;
    latitude?: number;   // NEW
    longitude?: number;  // NEW
    matchedAddress?: string; // NEW - to show what Nominatim returned
  } | null;

// Identical to the backend `round()` in intent.ts so cart pricing matches the
// server bit-for-bit. Rounds half-up to 2 decimal places (pence); scaling to
// cents and stripping float noise below 1e-6 of a cent keeps it exact at all
// realistic order magnitudes.
const round = (num: number): number => {
    const cents = Math.round(num * 100 * 1e6) / 1e6;
    return Math.round(cents) / 100;
};

const CartComponent = (props: Props) => {
    const [loading, setLoading] = useState(true);
    const currencySymbol = getRemoteEnv().currencySymbol || "A";
    const [additionalCharges, setAdditionalCharges] = React.useState<any[]>([]);
    const [subtotal, setSubtotal] = React.useState(0);
    const [total, setTotal] = React.useState(0);
    const [tipPageView, setTipPageView] = React.useState(false);
    const [paymentPageView, setPaymentPageView] = React.useState(false);

    // Stripe's scripts (~1MB with its workers) only load once the customer
    // actually reaches the payment step. Loading it eagerly meant every cold
    // menu visitor paid that bandwidth before first paint — the cart panel is
    // mounted (hidden) on the menu page too. Created once and kept in a ref
    // so re-renders never re-create the promise; the paymentPageView render
    // below already shows a spinner while the promise is still null/pending.
    const stripePromiseRef = React.useRef<any>(null);
    if (paymentPageView && !stripePromiseRef.current && props.singleStoreConfig) {
        if ((props.testMode || props.singleStoreConfig.stripeLiveId === "") && getRemoteEnv().stripeTestKey) {
            stripePromiseRef.current = loadStripe(getRemoteEnv().stripeTestKey, {stripeAccount: props.singleStoreConfig.stripeTestId});
        }
        else if (getRemoteEnv().stripeLiveKey) {
            stripePromiseRef.current = loadStripe(getRemoteEnv().stripeLiveKey, {stripeAccount: props.singleStoreConfig.stripeLiveId});
        }
    }
    const stripePromise = stripePromiseRef.current;
    const [orderType, setOrderType] = React.useState("");
    const [availableOrderTypes, setAvailableOrderTypes] = React.useState<string[]>([]);
    const [deliveryAddress, setDeliveryAddress] = React.useState<Address>(null);
    const [tableNumber, setTableNumber] = React.useState<number | null>(null);
    const [deliveryQuote, setDeliveryQuote] = React.useState<any>(null);
    const [isLoadingQuote, setIsLoadingQuote] = React.useState(false);
    const [quoteRefreshTrigger, setQuoteRefreshTrigger] = useState(0);
    const quoteTimerRef = useRef<NodeJS.Timeout | null>(null);
    const [isQuoteExpired, setIsQuoteExpired] = useState(false);
    
    // Tip states
    const [tipAmount, setTipAmount] = React.useState(0);
    const [tipType, setTipType] = React.useState<'percentage' | 'custom' | 'none' | null>(null);
    const [selectedTipPercentage, setSelectedTipPercentage] = React.useState<number | null>(null);
    const [customTipInput, setCustomTipInput] = React.useState<string>("");

    // const addressService = PostcodeServiceFactory.getPostcodeService();

    const { theme } = useTheme();
    // Fulfilment comes from the URL (?mode=) — the cart consumes it rather
    // than owning selection buttons. Missing mode = show the chooser.
    const orderModeCtx = useOrderMode();

    // Keep the internal orderType (which all pricing/quotes key off) in
    // step with the URL mode whenever the store supports it.
    React.useEffect(() => {
        if (orderModeCtx.mode && availableOrderTypes.includes(orderModeCtx.mode) && orderModeCtx.mode !== orderType) {
            setOrderType(orderModeCtx.mode);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderModeCtx.mode, availableOrderTypes]);


    const refreshDeliveryQuote = () => {
        console.log("Triggering delivery quote refresh...");
        setQuoteRefreshTrigger(prev => prev + 1);
    };

    React.useEffect(() => {
        const fetchDeliveryQuote = async () => {
            if (deliveryAddress?.postcode && props.singleStoreConfig && orderType === "delivery") {
                console.log("Fetching delivery quote...");
                setIsLoadingQuote(true);
                setIsQuoteExpired(false); // Reset expiry state

                const functions = getFunctions(FIREBASE_APP(), getRemoteEnv().region)
                const getDeliveryQuote = httpsCallableWithAppId(functions, "getDeliveryQuote");

                try {
                    const newQuote = await getDeliveryQuote({
                        store: props.singleStoreConfig.id, 
                        longitude: deliveryAddress.longitude, 
                        latitude: deliveryAddress.latitude, 
                        deliveryAddress: deliveryAddress, 
                        isLiveMode: !props.testMode && props.singleStoreConfig.stripeLiveId !== ""
                    });
                    
                    setDeliveryQuote(newQuote.data);
                    console.log("Delivery quote fetched: ", newQuote.data);
                    
                    // Clear any existing timer
                    if (quoteTimerRef.current) {
                        clearTimeout(quoteTimerRef.current);
                    }
                    
                    // Set 5-minute timer to mark quote as expired (don't auto-refresh)
                    quoteTimerRef.current = setTimeout(() => {
                        console.log("5 minutes elapsed - marking quote as expired");
                        setIsQuoteExpired(true);
                    }, 5 * 60 * 1000); // 5 minutes
                    
                } catch (error) {
                    console.error("Error fetching delivery quote:", error);
                    setDeliveryQuote(null);
                } finally {
                    setIsLoadingQuote(false);
                }
            } else if (orderType !== "delivery") {
                // Clear quote and timer if not delivery
                setDeliveryQuote(null);
                setIsLoadingQuote(false);
                setIsQuoteExpired(false);
                if (quoteTimerRef.current) {
                    clearTimeout(quoteTimerRef.current);
                }
            }
        };

        fetchDeliveryQuote();

        // Cleanup timer on unmount
        return () => {
            if (quoteTimerRef.current) {
                clearTimeout(quoteTimerRef.current);
            }
        };
    }, [deliveryAddress, props.singleStoreConfig, orderType, quoteRefreshTrigger]);


    const calculateTaxableAmountFromCart = () => {
        try {
            const key: 'taxableAmount' | 'takeoutTaxableAmount' = orderType !== "dinein"
                ? 'takeoutTaxableAmount'
                : 'taxableAmount';

            return props.cart.reduce((total, cartItem) => {
                let itemTaxable = 0;
                if (cartItem[key] !== undefined) itemTaxable += Number(cartItem[key]) || 0;
                if (cartItem.additionalOptionsMetadata && Array.isArray(cartItem.additionalOptionsMetadata)) {
                    for (const addon of cartItem.additionalOptionsMetadata) {
                        itemTaxable += Number(addon[key]) || 0;
                    }
                }
                return total + itemTaxable * (Number(cartItem.quantity) || 1);
            }, 0);
        } catch (error) {
            console.error('[TAX] Error calculating taxable amount:', error);
            return 0;
        }
    };


    React.useEffect(() => {console.log("Saved delivery address: ", deliveryAddress)}, [deliveryAddress])

    React.useEffect(() => {
        console.log("po: ", props.singleStoreConfig)

        const savedAddress = localStorage.getItem('deliveryAddress');
        if (savedAddress) {
        try {
            const parsedAddress = JSON.parse(savedAddress) as Address;
            setDeliveryAddress(parsedAddress);
        } catch (error) {
            setDeliveryAddress(null);
        }
        }

        // Get table number from URL parameters
        const saved = sessionStorage.getItem("tableNumber");
        if (saved) {
        const tableNum = parseInt(saved, 10);
        if (!isNaN(tableNum)) {
            setTableNumber(tableNum);
        }
        }
    }, []);

    React.useEffect(() => {
        console.log("SSC!", props.singleStoreConfig)
    }, [props.singleStoreConfig])

    React.useEffect(() => {
        console.log("address validation check")
        const validateAddress = async () => {
        if (deliveryAddress?.postcode && props.singleStoreConfig) {
            console.log("checking if can deliver")

            const functions = getFunctions(FIREBASE_APP(), getRemoteEnv().region)
            const getDeliveryQuote = httpsCallableWithAppId(functions, "getDeliveryQuote");

            if (orderType === "delivery") {
                if (!deliveryQuote) {
                    let newQuote = await getDeliveryQuote({store: props.singleStoreConfig.id, longitude: deliveryAddress.longitude, latitude: deliveryAddress.latitude, deliveryAddress: deliveryAddress, isLiveMode: !props.testMode && props.singleStoreConfig.stripeLiveId !== ""});
                    setDeliveryQuote(newQuote.data);
                    console.log("new delivery quote response: ", newQuote.data);
                }
                else if (deliveryQuote) {
                    if (deliveryQuote.customerLongitude !== deliveryAddress.longitude || deliveryQuote.customerLatitude !== deliveryAddress.latitude || deliveryQuote.deliveryType !== "in-house") {
                        let newQuote = await getDeliveryQuote({store: props.singleStoreConfig.id, longitude: deliveryAddress.longitude, latitude: deliveryAddress.latitude, deliveryAddress: deliveryAddress, isLiveMode: !props.testMode && props.singleStoreConfig.stripeLiveId !== ""});
                        setDeliveryQuote(newQuote.data);
                        console.log("updated delivery quote response: ", newQuote.data);
                        console.log("delivery quote: before and after longitude: ", deliveryQuote.customerLongitude, deliveryAddress.longitude, deliveryAddress.longitude === deliveryQuote.customerLongitude)
                    }
                    else {
                        console.log("already have a delivery quote for this address: ", deliveryQuote);
                    }
                }
            }

        }
        else {
            console.log("some issue")
        }
        };
    
        validateAddress();
    }, [deliveryAddress, props.singleStoreConfig, orderType]);

    React.useEffect(() => {
        const checkStoreOpenings = async () => {
            if (props.singleStoreConfig) {
                // Check if table number exists in session storage
                const hasTableNumber = !!sessionStorage.getItem("tableNumber");
                
                let availableTypes: string[] = [];
                let defaultType = "";

                if (props.singleStoreConfig.dinein?.enabled) {
                    const dineinOpen = await TimeService.checkStoreIsOpen([props.singleStoreConfig.dinein.timings]);
                    if (dineinOpen) {
                        availableTypes.push("dinein");
                        // ONLY set dine-in as default if table number exists in session storage
                        if (hasTableNumber && !defaultType) {
                            defaultType = "dinein";
                        }
                    }
                }
                if (props.singleStoreConfig.pickup?.enabled) {
                    const pickupOpen = await TimeService.checkStoreIsOpen([props.singleStoreConfig.pickup.timings]);
                    if (pickupOpen) {
                        availableTypes.push("pickup");
                        if (!defaultType) {
                            defaultType = "pickup";
                        }
                    }
                }
                if (props.singleStoreConfig.delivery?.enabled) {
                    const deliveryOpen = await TimeService.checkStoreIsOpen([props.singleStoreConfig.delivery.timings]);
                    if (deliveryOpen) {
                        availableTypes.push("delivery");
                        if (!defaultType) {
                            defaultType = "delivery";
                        }
                    }
                }
                
                // The URL's ?mode= wins whenever the store is open for it —
                // the legacy default chain above only applies without one.
                if (orderModeCtx.mode && availableTypes.includes(orderModeCtx.mode)) {
                    defaultType = orderModeCtx.mode;
                }

                setAvailableOrderTypes(availableTypes);
                setOrderType(defaultType);
                setLoading(false);
            }
        };

        checkStoreOpenings();
    }, [props.singleStoreConfig]);

    React.useEffect(() => {
        if (props.productData.length > 0 && props.additionalCharges) {
            let continuingTotal = 0;
            let total = 0;
            let addonCharges: any[] = [];
            let errorState = false;

            props.cart.map((item) => {
                let mainProduct = props.productData.filter((product) => product.productId === item.id)[0]

                if (!mainProduct || errorState) {
                    errorState = true;
                    props.setCart([]);
                }
                else if (item.loyaltyReward) {
                    // Loyalty reward line — paid in points at checkout,
                    // contributes nothing to the money subtotal.
                }
                else {
                    let additionalProducts = props.productData.filter((product) => item.additionalOptionIds.includes(product.productId))
                    let totalPrice = mainProduct.productPrice || 0;

                    for (let subproduct of additionalProducts) {
                        totalPrice += (subproduct.productPrice || 0);
                    }

                    continuingTotal += totalPrice * item.quantity
                }
            })

            if (!errorState) {
                continuingTotal = round(continuingTotal);

                addonCharges.push({name: "Subtotal", amount: continuingTotal})
                total += continuingTotal;
                setSubtotal(continuingTotal);

                // ── Step 1: Separate charges ─────────────────────────────
                const nonTaxRegularCharges = props.additionalCharges.filter((x) =>
                    x.applicableOn.includes(orderType) &&
                    x.enabled &&
                    x.name !== "Taxes" &&
                    x.name !== "Procurement Fee" &&
                    x.name !== "Card Fee"
                );
                const taxCharge = props.additionalCharges.find((x) =>
                    x.applicableOn.includes(orderType) && x.enabled && x.name === "Taxes"
                );
                const totalBasedCharges = props.additionalCharges.filter((x) =>
                    x.applicableOn.includes(orderType) &&
                    x.enabled &&
                    (x.name === "Procurement Fee" || x.name === "Card Fee")
                );

                // ── Step 2: Non-tax regular charges ──────────────────────────
                let nonTaxChargesTotal = 0;
                nonTaxRegularCharges.forEach((charge: any) => {
                    if (charge.name === "Delivery Fee") {
                        if (!deliveryAddress || !deliveryQuote || !deliveryQuote.allowedToDeliver ||
                            !(deliveryAddress.latitude === deliveryQuote.customerLatitude) ||
                            !(deliveryAddress.longitude === deliveryQuote.customerLongitude)) {
                            return;
                        }
                        let deliveryPrice = deliveryQuote.price;
                        if (deliveryQuote.freeDeliveryAbove && continuingTotal >= deliveryQuote.freeDeliveryAbove) {
                            deliveryPrice = 0;
                        }
                        addonCharges.push({ name: charge.name, amount: deliveryPrice, description: charge.description || undefined, alias: charge.alias || undefined });
                        total += deliveryPrice;
                        nonTaxChargesTotal += deliveryPrice;
                        return;
                    }

                    const amount = Number(charge.amount);
                    const base   = Number(charge.base || 0);
                    let calculatedAmount = 0;
                    let shouldAdd = true;

                    if (charge.operation === "%") {
                        calculatedAmount = round(base + (continuingTotal * amount / 100));
                        if (charge.name === "Platform Fee" && !props.singleStoreConfig[orderType]?.platformChargePaidByCustomer) shouldAdd = false;
                    } else if (charge.operation === "*") {
                        calculatedAmount = round(base + (continuingTotal * amount));
                        if (charge.name === "Platform Fee" && !props.singleStoreConfig[orderType]?.platformChargePaidByCustomer) shouldAdd = false;
                    } else if (charge.operation === "+") {
                        calculatedAmount = round(amount);
                        if (charge.name === "Platform Fee" && !props.singleStoreConfig[orderType]?.platformChargePaidByCustomer) shouldAdd = false;
                    } else return;

                    if (shouldAdd) {
                        addonCharges.push({ name: charge.name, amount: calculatedAmount, description: charge.description || undefined, alias: charge.alias || undefined });
                        total += calculatedAmount;
                        nonTaxChargesTotal += calculatedAmount;
                    }
                });

                // ── Step 3: Tax on cart taxable amount + non-tax charges ──────
                if (taxCharge) {
                    const cartTaxableAmount  = calculateTaxableAmountFromCart();
                    const totalTaxableAmount = cartTaxableAmount + nonTaxChargesTotal;
                    // Added on top → rate × base; included in prices → the tax
                    // inside the base (service/TaxMath.ts, same rule as the pool).
                    const calculatedTax = taxFromCharge(taxCharge, totalTaxableAmount);

                    if (taxCharge.taxIncludedInPrice) {
                        // Tax included in prices — show for info but don't add to total
                        addonCharges.push({ name: taxCharge.name, amount: calculatedTax, description: taxCharge.description || undefined, alias: taxCharge.alias || undefined, taxIncluded: true });
                    } else {
                        addonCharges.push({ name: taxCharge.name, amount: calculatedTax, description: taxCharge.description || undefined, alias: taxCharge.alias || undefined });
                        total += calculatedTax;
                    }
                }

                // ── Step 4: Total-based charges ───────────────────────────────
                totalBasedCharges.forEach((charge: any) => {
                    const amount = Number(charge.amount);
                    const base   = Number(charge.base || 0);
                    let calculatedAmount = 0;

                    if (charge.operation === "%") {
                        const rate = amount / 100;
                        calculatedAmount = round((rate * total + base) / (1 - rate));
                    } else if (charge.operation === "*") {
                        calculatedAmount = round(base + (total * amount));
                    } else if (charge.operation === "+") {
                        calculatedAmount = round(amount);
                    } else return;

                    addonCharges.push({ name: charge.name, amount: calculatedAmount, description: charge.description || undefined, alias: charge.alias || undefined });
                    total += calculatedAmount;
                });

                // Add tip to charges and total
                if (tipAmount > 0) {
                    addonCharges.push({
                        name: "Tip",
                        amount: tipAmount
                    });
                    total += tipAmount;
                }

                setTotal(round(total));
                setAdditionalCharges(addonCharges);
            }
        }
    }, [deliveryAddress, props.cart, props.productData, props.additionalCharges, orderType, tipAmount, deliveryQuote])

    // Recalculate percentage-based tips when subtotal changes
    React.useEffect(() => {
        if (tipType === 'percentage' && selectedTipPercentage !== null && subtotal > 0) {
            const calculatedTip = Math.round((subtotal * selectedTipPercentage / 100) * 100) / 100;
            setTipAmount(calculatedTip);
        }
    }, [subtotal, tipType, selectedTipPercentage]);

    const handleTipPercentageClick = (percentage: number) => {
        setTipType('percentage');
        setSelectedTipPercentage(percentage);
        setCustomTipInput("");
        const calculatedTip = Math.round((subtotal * percentage / 100) * 100) / 100;
        setTipAmount(calculatedTip);
    };

    const handleCustomTipChange = (value: string) => {
        setCustomTipInput(value);
        const numValue = parseFloat(value);
        
        if (value === "" || isNaN(numValue)) {
            setTipType(null);
            setSelectedTipPercentage(null);
            setTipAmount(0);
        } else {
            setTipType('custom');
            setSelectedTipPercentage(null);
            setTipAmount(Math.round(numValue * 100) / 100);
        }
    };

    const handleNoTipClick = () => {
        setTipType('none');
        setSelectedTipPercentage(null);
        setTipAmount(0);
        setCustomTipInput("");
    };

    function arraysEqual<T>(arr1: T[], arr2: T[]): boolean {
        // Check if arrays have the same length
        if (arr1.length !== arr2.length) {
          return false;
        }
      
        // Helper function to deep compare two values
        const deepEqual = (a: any, b: any): boolean => {
          if (a === b) return true;
          
          if (a == null || b == null) return a === b;
          
          if (typeof a !== typeof b) return false;
          
          if (typeof a === 'object') {
            if (Array.isArray(a) !== Array.isArray(b)) return false;
            
            if (Array.isArray(a)) {
              if (a.length !== b.length) return false;
              return a.every((item, index) => deepEqual(item, b[index]));
            }
            
            const keysA = Object.keys(a);
            const keysB = Object.keys(b);
            
            if (keysA.length !== keysB.length) return false;
            
            return keysA.every(key => keysB.includes(key) && deepEqual(a[key], b[key]));
          }
          
          return false;
        };
      
        // Create a copy of arr2 to track which items we've matched
        const arr2Copy = [...arr2];
        
        // For each item in arr1, find and remove a matching item from arr2Copy
        for (const item1 of arr1) {
          const matchIndex = arr2Copy.findIndex(item2 => deepEqual(item1, item2));
          
          if (matchIndex === -1) {
            return false; // No matching item found
          }
          
          // Remove the matched item to prevent duplicate matching
          arr2Copy.splice(matchIndex, 1);
        }
        
        // If we've matched all items, arr2Copy should be empty
        return arr2Copy.length === 0;
    }
    
    const handlePayPress = async () => {
        console.log("11- INSIDE HANDLE PAY PRESS")

        // Venue paused online ordering (switch / dated closure).
        if (orderModeCtx.pause) {
            ToastService.showToast(orderModeCtx.pause.message, { variant: "warning" });
            return;
        }
        
        if (props.cart.length > 0) {
            let allowedMenus = await TimeService.getAllowedMenus(props.menuSettings);
            let updatedCart = false;

            for (let orderItem of props.cart) {
                let itemObj = props.productData.filter((x) => x.productId === orderItem.id)[0];
                let menuFound = false;
                for (let menuItem of orderItem.menus) {
                    console.log("OI: ", orderItem)
                    if (allowedMenus.includes(menuItem)) {
                        let categoryObj = props.menuSettings.filter(x => x.menuName === menuItem)[0].categories.filter((c: any) => c.id === itemObj.category)[0];
                        if (categoryObj.enabled !== false && !categoryObj.deleted){
                            menuFound = true;
                        }
                    }
                }

                if (menuFound == false || itemObj.isDeleted || itemObj.isDisabled || itemObj.outOfStock) {
                    updatedCart = true;
                    updateQuantity(orderItem, 0);
                }

                // Validate user choices for main product
                if (itemObj.userChoices && itemObj.userChoices.length > 0) {
                    const selectedChoices = orderItem.selectedChoices || [];
                    const mainProductChoices = selectedChoices.filter((sc: any) => !sc.parent);
                    
                    for (const selected of mainProductChoices) {
                        const choiceDef = itemObj.userChoices.find((uc: any) => uc.name === selected.name);
                        
                        if (!choiceDef || !choiceDef.choices.includes(selected.value)) {
                            updatedCart = true;
                            updateQuantity(orderItem, 0);
                            break;
                        }
                    }
                    
                    for (const choice of itemObj.userChoices) {
                        const selectedForThisChoice = mainProductChoices.filter(
                            (selected: any) => selected.name === choice.name
                        );
                        
                        if (choice.type === 'SELECT_ONE' && selectedForThisChoice.length > 1) {
                            updatedCart = true;
                            updateQuantity(orderItem, 0);
                            break;
                        }
                        
                        if (choice.type === 'SELECT_LIMIT' && selectedForThisChoice.length > (choice.limit || 1)) {
                            updatedCart = true;
                            updateQuantity(orderItem, 0);
                            break;
                        }
                    }
                }

                if (orderItem.additionalOptionIds && orderItem.additionalOptionIds.length > 0) {
                    for (let addOption of orderItem.additionalOptionIds) {
                        let additionalOptionObj = props.productData.filter((x) => x.productId === addOption)[0];
                        if (!additionalOptionObj.outOfStock && !additionalOptionObj.isDisabled && !additionalOptionObj.isDeleted) {

                            let addOptionsCategoryObj = props.menuSettings.filter(x => x.menuName === additionalOptionObj.menus[0])[0].categories.filter((c: any) => c.id === additionalOptionObj.category)[0];
                            if (addOptionsCategoryObj.enabled !== false && !addOptionsCategoryObj.deleted){
                                if (additionalOptionObj.userChoices && additionalOptionObj.userChoices.length > 0) {
                                    const addonChoices = (orderItem.selectedChoices || []).filter(
                                        (sc: any) => sc.parent === addOption
                                    );
                                    
                                    let invalidAddonChoice = false;
                                    for (const selected of addonChoices) {
                                        const choiceDef = additionalOptionObj.userChoices.find((uc: any) => uc.name === selected.name);
                                        
                                        if (!choiceDef || !choiceDef.choices.includes(selected.value)) {
                                            removeOption(orderItem, addOption);
                                            updatedCart = true;
                                            invalidAddonChoice = true;
                                            break;
                                        }
                                    }
                                    
                                    if (!invalidAddonChoice) {
                                        for (const choice of additionalOptionObj.userChoices) {
                                            const selectedForThisChoice = addonChoices.filter(
                                                (selected: any) => selected.name === choice.name
                                            );
                                            
                                            if (choice.type === 'SELECT_ONE' && selectedForThisChoice.length > 1) {
                                                removeOption(orderItem, addOption);
                                                updatedCart = true;
                                                break;
                                            }
                                            
                                            if (choice.type === 'SELECT_LIMIT' && selectedForThisChoice.length > (choice.limit || 1)) {
                                                removeOption(orderItem, addOption);
                                                updatedCart = true;
                                                break;
                                            }
                                        }
                                    }
                                }
                            }
                            else {
                                removeOption(orderItem, addOption);
                                updatedCart = true;
                            }

                        }
                        else {
                            removeOption(orderItem, addOption)
                            updatedCart = true;
                        }
                    }
                }
            }
            
            // Item count tracking: lines that would take MORE of a tracked
            // product than remains (across the whole cart, add-ons included)
            // are removed — outright sold-out items were already dropped by
            // the outOfStock check above. PPB re-validates server-side.
            const need = new Map<string, { qty: number; lines: any[] }>();
            for (const orderItem of props.cart) {
                if (!orderItem.quantity) continue;
                const q = Number(orderItem.quantity) || 1;
                const track = (pid: string) => {
                    const e = need.get(pid) || { qty: 0, lines: [] };
                    e.qty += q;
                    if (!e.lines.includes(orderItem)) e.lines.push(orderItem);
                    need.set(pid, e);
                };
                if (orderItem.id) track(orderItem.id);
                for (const addonId of orderItem.additionalOptionIds || []) if (addonId) track(addonId);
            }
            for (const [pid, entry] of Array.from(need.entries())) {
                const p = props.productData.find((x: any) => x.productId === pid);
                if (!p || typeof p.stockCount !== "number") continue;
                if (entry.qty > p.stockCount) {
                    for (const line of entry.lines) updateQuantity(line, 0);
                    updatedCart = true;
                }
            }

            if (!updatedCart) {
                setTipPageView(true);
            }
            else {
                ToastService.showToast(`Unavailable items have been removed from your cart. Please check cart before proceeding!`, {
                    variant: "error",
                });
            }
        }
    }

    const handleGetDeliveryQuote = () => {
        console.log("User requesting new delivery quote...");
        refreshDeliveryQuote();
    };

    const handleContinueToPayment = () => {
        if (orderModeCtx.pause) {
            setTipPageView(false);
            ToastService.showToast(orderModeCtx.pause.message, { variant: "warning" });
            return;
        }
        setTipPageView(false);
        setPaymentPageView(true);
    };

    const handleBackFromTip = () => {
        setTipPageView(false);
        // Reset tip if user goes back
        setTipAmount(0);
        setTipType(null);
        setSelectedTipPercentage(null);
        setCustomTipInput("");
    };

    const updateQuantity = (existingProduct: any, newQuantity: number) => {
        // Item count tracking: raising a line's quantity can't take tracked
        // products (the line's item AND its add-ons, once per unit) past
        // what's left — counted across the whole cart, same rule as the
        // add-to-cart gate. Decreases always pass.
        if (newQuantity > (Number(existingProduct?.quantity) || 0)) {
            const sameLine = (p: any) =>
                p.id === existingProduct.id &&
                arraysEqual(p.additionalOptionIds, existingProduct.additionalOptionIds) &&
                arraysEqual(p.selectedChoices, existingProduct.selectedChoices) &&
                p.additionalRequests === existingProduct.additionalRequests;
            const need = new Map<string, number>();
            for (const line of props.cart) {
                const qty = sameLine(line) ? newQuantity : (Number(line.quantity) || 0);
                if (line.id) need.set(line.id, (need.get(line.id) || 0) + qty);
                for (const addonId of line.additionalOptionIds || []) {
                    if (addonId) need.set(addonId, (need.get(addonId) || 0) + qty);
                }
            }
            // Judge ONLY the products the CHANGED line touches — an overdraft
            // on some other line must not block adjusting this one.
            const involved = new Set<string>([existingProduct?.id, ...(existingProduct?.additionalOptionIds || [])].filter(Boolean));
            for (const [pid, qty] of Array.from(need.entries())) {
                if (!involved.has(pid)) continue;
                const p = props.productData.find((x: any) => x.productId === pid);
                if (p && typeof p.stockCount === "number" && qty > p.stockCount) {
                    ToastService.showToast(
                        p.stockCount <= 0 ? `${p.productName} has just sold out` : `Only ${p.stockCount} of ${p.productName} left`,
                        { variant: "warning" }
                    );
                    return;
                }
            }
        }

        let existingItem: any;

        for (let product of props.cart) {
            if (product.id === existingProduct.id && arraysEqual(product.additionalOptionIds, existingProduct.additionalOptionIds) && arraysEqual(product.selectedChoices, existingProduct.selectedChoices) && product.additionalRequests === existingProduct.additionalRequests) {
                existingItem = product;
                existingItem.quantity = newQuantity;
            }
        }

        if (existingItem) {
            if (existingItem.quantity > 0) {
                props.setCart(prevCart => prevCart.map((p) => (p.id === existingItem.id && arraysEqual(p.additionalOptionIds, existingItem.additionalOptionIds) && arraysEqual(p.selectedChoices, existingItem.selectedChoices) && p.additionalRequests === existingItem.additionalRequests) ? existingItem : p))
            }
            else {
                props.setCart(prevCart => prevCart.filter((p) => !(p.id === existingItem.id && arraysEqual(p.additionalOptionIds, existingItem.additionalOptionIds) && arraysEqual(p.selectedChoices, existingItem.selectedChoices) && p.additionalRequests === existingItem.additionalRequests)))
            }
        }
    }

    const removeOption = (existingProduct: any, optionToRemove: string) => {
        props.setCart(prevCart => 
            prevCart.map(p => {
                if (p.id === existingProduct.id && 
                    arraysEqual(p.additionalOptionIds, existingProduct.additionalOptionIds) && 
                    arraysEqual(p.selectedChoices, existingProduct.selectedChoices) && 
                    p.additionalRequests === existingProduct.additionalRequests) {
                    return {
                        ...p,
                        additionalOptionIds: p.additionalOptionIds.filter((id: string) => id !== optionToRemove)
                    };
                }
                return p;
            })
        );
    };

    const deliveryCharge         = props.additionalCharges?.find((c: any) => c.name === 'Delivery Fee');
    const minimumDeliverySpend   = deliveryCharge?.minimumDeliverySpend || 0;
    const freeDeliveryAboveValue = deliveryQuote?.freeDeliveryAbove || 0;
    const belowMinimumSpend      = orderType === 'delivery' && minimumDeliverySpend > 0 && subtotal < minimumDeliverySpend;

    // Paused by the venue → back from the tip step to the cart (the
    // context already toasted). The card form itself is left alone: a
    // payment may be mid-flight there.
    React.useEffect(() => {
        if (orderModeCtx.pause && tipPageView) setTipPageView(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderModeCtx.pause, tipPageView]);

    const canPay = props.cart.length > 0 &&
                   !orderModeCtx.pause &&
                   !loading &&
                   orderType &&
                   !belowMinimumSpend &&
                   (orderType !== "delivery" || (
                       orderType === "delivery" &&
                       deliveryAddress &&
                       deliveryQuote &&
                       !isLoadingQuote &&
                       !isQuoteExpired &&
                       deliveryQuote.allowedToDeliver &&
                       deliveryAddress.latitude === deliveryQuote.customerLatitude &&
                       deliveryAddress.longitude === deliveryQuote.customerLongitude
                   ));

    const shouldShowGetQuoteButton = orderType === "delivery" && 
                    deliveryAddress && 
                    isQuoteExpired && 
                    !isLoadingQuote &&
                    props.cart.length > 0;

    // Store config still loading on an ordering site with no mode yet —
    // hold a spinner so the cart doesn't flash before the chooser.
    if (!getRemoteEnv().menuOnly && !orderModeCtx.ready && !orderModeCtx.mode) {
        return (
            <div className="cart-outer" style={{display: "flex", flexGrow: 1, alignItems: "center", justifyContent: "center", minHeight: "50vh"}}>
                <Spinner color={theme.loaderColor} />
            </div>
        );
    }

    // No fulfilment chosen (deep link to /cart, or "Change" pressed) —
    // show the chooser in place of the cart until one is picked.
    if (orderModeCtx.orderingEnabled && orderModeCtx.ready && !orderModeCtx.mode) {
        return (
            <div className="cart-outer" style={{display: "flex", flexGrow: 1, flexDirection: "column", overflowY: "auto"}}>
                <OrderModeSelect />
            </div>
        );
    }

    // Tip Page View
    if (tipPageView) {
        return (
            <>
            <div className="cart-outer" style={{display: "flex", color: theme.cartTextColor, flexGrow: "1", flexShrink: "1", flexDirection: "column", padding: "20px", overflowY: "scroll"}}>
                <button 
                    style={{
                        width: "auto",
                        padding: "8px 16px",
                        cursor: "pointer",
                        backgroundColor: theme.cartButtonColor,
                        color: theme.cartButtonTextColor,
                        border: "none",
                        borderRadius: "10px",
                        fontSize: "14px",
                        fontWeight: "500",
                        alignSelf: "flex-start",
                        transition: "transform 0.2s ease",
                    }} 
                    onClick={handleBackFromTip}
                    >
                    Back
                </button>
                <h2 style={{fontWeight: 800, letterSpacing: "-0.4px", fontSize: "24px", margin: "16px 0 4px"}}>Add a tip</h2>
                <p style={{margin: "0 0 20px", fontSize: "14px", lineHeight: 1.5, color: theme.cartTextColor, opacity: .65}}>
                    {orderType === "delivery"
                        ? "Show your appreciation — 100% of your tip goes to the delivery driver."
                        : "Show your appreciation for great service — your tip goes directly to the staff."}
                </p>

                {/* Tip selection: percentages in a grid, then No tip. Selected
                    state = brand pay colour; idle stays theme-neutral so dark
                    themes work. */}
                <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "10px"}}>
                    {[10, 15, 20, 25].map(percentage => {
                        const calculatedAmount = Math.round((subtotal * percentage / 100) * 100) / 100;
                        const on = selectedTipPercentage === percentage;
                        return (
                            <button
                                key={percentage}
                                onClick={() => handleTipPercentageClick(percentage)}
                                style={{
                                    padding: "14px 12px",
                                    backgroundColor: on ? theme.payButtonColor : "transparent",
                                    color: on ? theme.payButtonTextColor : theme.cartTextColor,
                                    border: `1.5px solid ${on ? theme.payButtonColor : theme.cartSummaryBorder}`,
                                    borderRadius: "14px",
                                    cursor: "pointer",
                                    fontFamily: "inherit",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: "3px",
                                    transition: "all 0.15s ease"
                                }}
                            >
                                <span style={{fontSize: "17px", fontWeight: 800, letterSpacing: "-0.3px"}}>{percentage}%</span>
                                <span style={{fontSize: "12.5px", fontWeight: 600, opacity: on ? .85 : .55}}>{currencySymbol}{calculatedAmount.toFixed(2)}</span>
                            </button>
                        );
                    })}
                </div>
                <button
                    onClick={handleNoTipClick}
                    style={{
                        padding: "13px",
                        width: "100%",
                        boxSizing: "border-box",
                        backgroundColor: tipType === 'none' ? theme.payButtonColor : "transparent",
                        color: tipType === 'none' ? theme.payButtonTextColor : theme.cartTextColor,
                        border: `1.5px solid ${tipType === 'none' ? theme.payButtonColor : theme.cartSummaryBorder}`,
                        borderRadius: "14px",
                        cursor: "pointer",
                        fontFamily: "inherit",
                        fontSize: "14.5px",
                        fontWeight: 700,
                        marginBottom: "18px",
                        transition: "all 0.15s ease"
                    }}
                >
                    No tip
                </button>

                {/* Custom Tip Input */}
                <div style={{marginBottom: "20px"}}>
                    <label style={{display: "block", marginBottom: "8px", fontSize: "12px", fontWeight: 700, letterSpacing: ".08em", opacity: .6}}>
                        CUSTOM AMOUNT
                    </label>
                    <input
                        type="number"
                        placeholder={`Enter custom tip (${currencySymbol})`}
                        value={customTipInput}
                        onChange={(e) => handleCustomTipChange(e.target.value)}
                        step="0.01"
                        min="0"
                        style={{
                            width: "100%",
                            padding: "13px 14px",
                            borderRadius: "14px",
                            border: `1.5px solid ${tipType === 'custom' ? theme.payButtonColor : theme.cartSummaryBorder}`,
                            backgroundColor: "transparent",
                            color: theme.cartTextColor,
                            boxSizing: "border-box",
                            fontSize: "16px",
                            fontFamily: "inherit",
                            outline: "none"
                        }}
                    />
                </div>

                {/* Order Summary */}
                <div style={{
                    borderTop: `1px solid ${theme.cartSummaryBorder}`,
                    paddingTop: "15px",
                    marginTop: "auto"
                }}>
                    {tipType !== null && (
                        <div style={{display: "flex", justifyContent: "space-between", marginBottom: "8px"}}>
                            <span>Tip</span>
                            <span>{currencySymbol}{tipAmount.toFixed(2)}</span>
                        </div>
                    )}
                    <div style={{display: "flex", justifyContent: "space-between", marginTop: tipType !== null ? "10px" : "0", paddingTop: tipType !== null ? "10px" : "0", borderTop: tipType !== null ? `1px solid ${theme.cartSummaryBorder}` : "none", fontWeight: "bold", fontSize: "18px"}}>
                        <span>Total</span>
                        <span>{currencySymbol}{total.toFixed(2)}</span>
                    </div>
                </div>
            </div>

            {/* Action Buttons */}
            <div style={{display: "flex", flexDirection: "column", flexShrink: "0"}}>
                <div 
                    onClick={tipType !== null ? handleContinueToPayment : undefined}
                    style={{
                        display: "flex",
                        width: "100%",
                        backgroundColor: tipType !== null ? theme.payButtonColor : "#cccccc",
                        color: tipType !== null ? theme.payButtonTextColor : "#666666",
                        justifyContent: "center",
                        alignItems: "center",
                        cursor: tipType !== null ? "pointer" : "not-allowed",
                        height: "54px",
                        boxSizing: "border-box",
                        fontWeight: 700,
                        fontSize: "15px",
                        letterSpacing: "0.01em"
                    }}
                >
                    Continue to Payment{tipAmount > 0 ? ` · ${currencySymbol}${total.toFixed(2)}` : ""}
                </div>
            </div>
            </>
        );
    }

    // Payment Page View
    if (paymentPageView) {
        // Show loading until both stripePromise and singleStoreConfig are ready
        if (!props.singleStoreConfig || !stripePromise) {
            return <Spinner color={theme.loaderColor} />
        }
        
        return (
            <Elements stripe={stripePromise}>
                <PaymentDetailsComponent 
                    amount={total} 
                    tipAmount={tipAmount}
                    order={props.cart} 
                    setCart={props.setCart} 
                    updateQuantity={updateQuantity} 
                    removeOption={removeOption}
                    productData={props.productData}
                    menuSettings={props.menuSettings} 
                    openingHours={props.openingHours} 
                    onBack={() => {
                        setPaymentPageView(false);
                        setTipPageView(true);
                        refreshDeliveryQuote();

                    }} 
                    onSuccessReturn={() => {
                        setPaymentPageView(false);
                        setTipPageView(false);
                        // Reset tip states
                        setTipAmount(0);
                        setTipType(null);
                        setSelectedTipPercentage(null);
                        setCustomTipInput("");
                        refreshDeliveryQuote();
                    }}
                    testMode={(props.testMode || props.singleStoreConfig.stripeLiveId === "") || false} 
                    orderType={orderType} 
                    deliveryAddress={deliveryAddress}
                    deliveryQuote={deliveryQuote}
                    tableNumber={tableNumber}
                />
            </Elements>
        )
    }

    // Main Cart View
    return (
        <>
        <div className="cart-outer" style={{display: "flex", color: theme.cartTextColor, flexGrow: "1", flexShrink: "1", flexDirection: "column", padding: "20px", overflowY: "scroll"}}>
            <div style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
                <span style={{display: "flex", alignItems: "center", marginRight: "10px", fontSize: "xx-large"}}><HiOutlineShoppingCart /></span>
                <h2 style={{fontWeight: "500"}}>My Cart</h2> 
            </div>

            <div style={{display: "flex", flexDirection: "column", flexGrow: "1"}}>
                {props.productData.length > 0 && 
                    <CartCards productData={props.productData} cart={props.cart} currencySymbol={currencySymbol} updateQuantity={updateQuantity}/>                    
                }

                {props.cart.length < 1 && 
                    <p style={{marginLeft: "15px"}}>You have no items in your cart.</p>
                }
            </div>
        </div>

        {props.cart.length > 0 && !loading && 
            <div style={{display: "flex", flexGrow: "0", minHeight: "50px", flexDirection: "column", color: theme.cartTextColor, borderTop: `1px solid ${theme.cartSummaryBorder}`, width: "100%", padding: "10px", boxSizing: "border-box", flexShrink: "0"}}> 
                {/* The fulfilment is chosen up front (?mode= in the URL) —
                    the cart just shows it; Change reopens the chooser. */}
                <div className="order-type-display" style={{display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px", justifyContent: "space-between"}}>
                    <span style={{display: "inline-flex", alignItems: "center", gap: "8px", fontSize: "14.5px", fontWeight: 700}}>
                        <span style={{display: "inline-flex", fontSize: "19px", color: theme.payButtonColor}}>
                            {(() => { const M = ORDER_MODES.find(m => m.id === orderType); return M ? <M.Icon /> : <HiOutlineShoppingCart />; })()}
                        </span>
                        {ORDER_MODES.find(m => m.id === orderType)?.label || "Select order type"}
                        {orderType && (orderModeCtx.pause || !availableOrderTypes.includes(orderType)) && (
                            <span style={{fontSize: "12px", fontWeight: 600, opacity: 0.6}}>{orderModeCtx.pause?.because === "switch" ? "(paused)" : "(currently closed)"}</span>
                        )}
                    </span>
                    {/* Change whenever more than one mode is ENABLED —
                        closed modes are selectable too (browsing model). */}
                    {orderModeCtx.orderingEnabled && orderModeCtx.enabledModes.length > 1 && (
                        <button
                            onClick={() => orderModeCtx.clearMode()}
                            style={{
                                padding: "6px 14px",
                                backgroundColor: "transparent",
                                color: theme.cartTextColor,
                                border: `1.5px solid ${theme.cartSummaryBorder}`,
                                borderRadius: "999px",
                                fontSize: "12.5px",
                                fontWeight: 700,
                                cursor: "pointer",
                                fontFamily: "inherit"
                            }}
                        >
                            Change
                        </button>
                    )}
                </div>

                {orderType === "delivery" && (
                    <DeliveryAddress
                        address={deliveryAddress}
                        setAddress={setDeliveryAddress} 
                    />
                )}

                {orderType === "dinein" && (
                    <div style={{padding: "10px", marginTop: "10px"}}>
                        <label style={{display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "500"}}>
                            Table Number
                        </label>
                        <input
                            type="number"
                            placeholder="Enter table number"
                            value={tableNumber !== null ? tableNumber : ""}
                            onChange={(e) => {
                            const value = e.target.value;
                            if (value === "") {
                                setTableNumber(null);
                                sessionStorage.removeItem("tableNumber");
                            } else {
                                const num = parseInt(value, 10);
                                if (!isNaN(num)) {
                                setTableNumber(num);
                                sessionStorage.setItem("tableNumber", String(num));
                                }
                            }
                            }}
                            min="1"
                            step="1"
                            style={{
                                width: "100%",
                                padding: "12px",
                                borderRadius: "8px",
                                border: "2px solid #ccc",
                                boxSizing: "border-box",
                                fontSize: "16px",
                                outline: "none"
                            }}
                        />
                    </div>
                )}
                
                {additionalCharges.filter((x) => (x.applicableOn ? x.applicableOn.includes(orderType) : true) && !x.taxIncluded).map((charge, index) => {
                    return (
                        <div key={index} style={{display: "flex", flexDirection: "row", width: "100%", marginTop: "5px"}}>
                            <span style={{display: "flex", flexGrow: "1"}}>{charge.alias || charge.name} &nbsp; {charge.description && <span data-tooltip-id="my-tooltip" data-tooltip-content={charge.description}>&#9432;</span>}</span>
                            {charge.name!=='Delivery Fee' && <span>{(charge.amount == 0 ? "FREE" : currencySymbol + charge.amount.toFixed(2))}</span>}
                            {charge.name==='Delivery Fee' && !isLoadingQuote && !isQuoteExpired && <span>{(charge.amount == 0 ? "FREE" : currencySymbol + charge.amount.toFixed(2))}</span>}
                        </div>
                    )
                })}

                <div style={{display: "flex", flexDirection: "row", width: "100%", marginTop: "10px"}}>
                    <span style={{display: "flex", flexGrow: "1", fontWeight: "bold"}}>Total</span>
                    <span style={{fontWeight: "bold"}}>{currencySymbol + total.toFixed(2)}</span>
                </div>

                {additionalCharges.filter((x) => x.taxIncluded).map((charge, index) => {
                    return (
                        <div key={index} style={{display: "flex", flexDirection: "row", width: "100%", marginTop: "6px", opacity: 0.6, fontSize: "0.85em"}}>
                            <span style={{display: "flex", flexGrow: "1"}}>{charge.alias || charge.name} (included)</span>
                            <span>{currencySymbol + charge.amount.toFixed(2)}</span>
                        </div>
                    )
                })}
            </div>
        }

        {props.cart.length > 0 && (orderType === "delivery" && deliveryAddress && deliveryQuote && !deliveryQuote.allowedToDeliver && !loading) &&
            <span style={{
                color: 'red',
                fontSize: '0.8rem',
                margin: '0 auto',
                marginTop: '-10px',
                marginBottom: '10px'
            }}>
                {deliveryQuote.status}
            </span>
        }

        {/* Show loading state when fetching quote */}
        {orderType === "delivery" && isLoadingQuote && props.cart.length > 0 &&
            <span style={{
                color: theme.cartTextColor,
                fontSize: '0.8rem',
                margin: '0 auto',
                marginTop: '-10px',
                marginBottom: '10px'
            }}>
                Calculating delivery pricing...
            </span>
        }

        {/* Show "Get Delivery Quote" button when quote is expired */}
        {shouldShowGetQuoteButton &&
            <div onClick={handleGetDeliveryQuote} style={{
                display: "flex", 
                width: "100%", 
                backgroundColor: theme.payButtonColor, 
                color: theme.payButtonTextColor, 
                justifyContent: "center", 
                alignItems: "center", 
                cursor: "pointer", 
                minHeight: "50px", 
                boxSizing: "border-box"
            }}>
                Get Delivery Quote
            </div>
        }

        {/* Free delivery ribbon */}
        {orderType === 'delivery' && freeDeliveryAboveValue > 0 && subtotal < freeDeliveryAboveValue &&
            <div style={{
                display: 'flex',
                width: '100%',
                backgroundColor: '#e8f5e9',
                color: '#2e7d32',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '8px',
                fontSize: '0.85rem',
                fontWeight: '600',
                boxSizing: 'border-box',
                marginBottom: '4px',
            }}>
                🚚 Free delivery for orders over {currencySymbol}{freeDeliveryAboveValue.toFixed(2)}
            </div>
        }

        {/* Minimum spend message */}
        {belowMinimumSpend &&
            <div style={{
                display: 'flex',
                width: '100%',
                backgroundColor: '#fff3e0',
                color: '#e65100',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '8px',
                fontSize: '0.85rem',
                fontWeight: '600',
                boxSizing: 'border-box',
                marginBottom: '4px',
            }}>
                Minimum {currencySymbol}{minimumDeliverySpend.toFixed(2)} spend required for delivery
            </div>
        }

        {/* Venue paused online ordering — their note + when it's back,
            right above the (disabled) pay button. */}
        {orderModeCtx.pause && props.cart.length > 0 &&
            <StorePauseBanner textColor={theme.cartTextColor} showNotice={false} style={{borderRadius: 0, marginBottom: "4px"}} />
        }

        {/* Show "Pay Now" button when quote is valid */}
        {canPay && !shouldShowGetQuoteButton &&
            <div onClick={handlePayPress} style={{
                display: "flex", 
                width: "100%", 
                backgroundColor: theme.payButtonColor, 
                color: theme.payButtonTextColor, 
                justifyContent: "center", 
                alignItems: "center", 
                cursor: "pointer", 
                minHeight: "50px", 
                boxSizing: "border-box"
            }}>
                Pay Now
            </div>
        }

        {/* Show disabled button when conditions not met */}
        {!canPay && !shouldShowGetQuoteButton &&
            <div style={{
                display: "flex", 
                width: "100%", 
                backgroundColor: "#616161", 
                color: "#939393", 
                justifyContent: "center", 
                alignItems: "center", 
                height: "50px", 
                boxSizing: "border-box"
            }}>
                {orderModeCtx.pause ? orderModeCtx.pause.button : isLoadingQuote ? "Calculating Delivery..." : belowMinimumSpend ? "Minimum spend not met" : "Pay Now"}
            </div>
        }
        

        {/* {props.cart.length > 0 && !loading && orderType &&  (orderType !== "delivery" || (orderType === "delivery" && (deliveryAddress && deliveryQuote && deliveryQuote.allowedToDeliver && (deliveryAddress.latitude === deliveryQuote.customerLatitude) && (deliveryAddress.longitude === deliveryQuote.customerLongitude)))) &&
            <div onClick={handlePayPress} style={{display: "flex", width: "100%", backgroundColor: theme.payButtonColor, color: theme.payButtonTextColor, justifyContent: "center", alignItems: "center", cursor: "pointer", minHeight: "50px", boxSizing: "border-box"}}>
                Pay Now
            </div>
        }

        {(props.cart.length < 1 || (orderType === "delivery" && (!deliveryAddress || !deliveryQuote || !deliveryQuote.allowedToDeliver || !(deliveryAddress.latitude === deliveryQuote.customerLatitude) || !(deliveryAddress.longitude === deliveryQuote.customerLongitude))) || !orderType) &&
            <div style={{display: "flex", width: "100%", backgroundColor: "#616161", color: "#939393", justifyContent: "center", alignItems: "center",  height: "50px", boxSizing: "border-box"}}>
                Pay Now
            </div>
        } */}

        <Tooltip id="my-tooltip" />
        </>
    )
}

export default CartComponent;